import { Box, Chip, alpha, useTheme } from '@mui/material';
import type { RiskKey } from '../../theme/theme';
import { titleCase } from '../../utils/format';

/** Risk severity badge — the product's signal colour, used everywhere risk appears. */
export function RiskBadge({ level, size = 'small' }: { level: string; size?: 'small' | 'medium' }) {
  const theme = useTheme();
  const key = (['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].includes(level) ? level : 'MEDIUM') as RiskKey;
  const color = theme.palette.risk[key];
  return (
    <Chip
      size={size}
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
          {titleCase(level)}
        </Box>
      }
      sx={{
        color,
        bgcolor: alpha(color, 0.14),
        border: `1px solid ${alpha(color, 0.35)}`,
        '& .MuiChip-label': { px: 0.9 },
      }}
    />
  );
}

const STATUS_COLORS: Record<
  string,
  'default' | 'info' | 'success' | 'warning' | 'error' | 'secondary'
> = {
  OPEN: 'error',
  ACKNOWLEDGED: 'warning',
  IN_PROGRESS: 'info',
  RESOLVED: 'success',
  CLOSED: 'default',
  GENERATED: 'info',
  ACTIONED: 'success',
  DISMISSED: 'default',
  SUPERSEDED: 'secondary',
  SUCCESS: 'success',
  FALLBACK: 'warning',
  FAILED: 'error',
  ACTIVE: 'success',
  DRAFT: 'warning',
  RETIRED: 'default',
  // Tenant, user, membership and vehicle lifecycle vocabularies.
  SUSPENDED: 'error',
  INACTIVE: 'default',
  MAINTENANCE: 'warning',
  ARCHIVED: 'default',
};

export function StatusBadge({
  status,
  label,
  size = 'small',
}: {
  status: string;
  /**
   * Wording that reads better than the stored enum, for ONE vocabulary.
   *
   * Added rather than renaming globally, because these vocabularies OVERLAP:
   * `ACKNOWLEDGED` is both a recommendation status and an incident status, and
   * a rename in this map would have reworded a screen nobody asked about. The
   * caller that owns the vocabulary supplies the word — recommendations do,
   * through `statusLabel` — and everything else keeps title-casing the enum.
   *
   * The TONE is still looked up from `status`, so a friendlier word cannot
   * accidentally change what colour a state is.
   */
  label?: string;
  size?: 'small' | 'medium';
}) {
  const theme = useTheme();
  const tone = STATUS_COLORS[status] ?? 'default';
  const color = tone === 'default' ? theme.palette.text.secondary : theme.palette[tone].main;
  return (
    <Chip
      size={size}
      variant="outlined"
      label={label ?? titleCase(status)}
      sx={{ color, borderColor: alpha(color, 0.4), bgcolor: alpha(color, 0.08), fontWeight: 600 }}
    />
  );
}

export function SourceBadge({ source }: { source: string }) {
  const theme = useTheme();
  const isLlm = source === 'LLM';
  const isFallback = source.includes('FALLBACK');
  const color = isLlm
    ? theme.palette.secondary.main
    : isFallback
      ? theme.palette.warning.main
      : theme.palette.text.secondary;
  return (
    <Chip
      size="small"
      label={isLlm ? 'AI-enhanced' : isFallback ? 'Deterministic fallback' : 'Deterministic'}
      sx={{
        color,
        bgcolor: alpha(color, 0.12),
        border: `1px solid ${alpha(color, 0.3)}`,
        fontWeight: 600,
      }}
    />
  );
}

/**
 * Tenant role badge.
 *
 * Kept visually distinct from StatusBadge — outlined rather than tinted — so a
 * role is never mistaken for a lifecycle state at a glance. Both carry a text
 * label; colour alone never conveys meaning.
 */
const ROLE_LABELS: Record<string, string> = {
  TENANT_ADMIN: 'Tenant Admin',
  OPERATIONS_MANAGER: 'Operations Manager',
  OPERATOR: 'Operator',
  VIEWER: 'Viewer',
};

export function RoleBadge({ role, size = 'small' }: { role: string; size?: 'small' | 'medium' }) {
  // An unknown role still renders readably rather than disappearing.
  const label = ROLE_LABELS[role] ?? titleCase(role);
  const emphasised = role === 'TENANT_ADMIN';
  return (
    <Chip
      size={size}
      label={label}
      variant="outlined"
      color={emphasised ? 'primary' : 'default'}
      aria-label={`Role: ${label}`}
      sx={{ fontWeight: emphasised ? 600 : 500, '& .MuiChip-label': { px: 0.9 } }}
    />
  );
}
